/**
 * The running call list, held for as long as the operator is working it down.
 *
 * A thin layer over `queue.ts`: that file decides which number is next, this
 * one keeps the answer in state and hands it to the thing that dials. Its own
 * file rather than more state inside `CallProvider`, which has been split once
 * already for the same reason - see `useSuppressedNumbers.ts`, which left for
 * exactly this door.
 *
 * The run does NOT survive the window being closed, and that is deliberate for
 * now: a queue that lives on invisibly is a queue nobody can find again, and
 * the plan (crm-telefonia-tegemata.md §5.4) puts saved and shared lists
 * explicitly after this step. Closing the window says so in its own button.
 */
import { useCallback, useState } from "react";
import type { CallTarget } from "../callState";
import { useSuppressedNumbers } from "../useSuppressedNumbers";
import { advanceQueue, buildQueue, queueAt, type CallQueue } from "./queue";

export interface CallQueueApi {
  /** The run, or null when the operator is making single calls. */
  queue: CallQueue | null;
  /**
   * Begin a run over these rows and dial the first survivor.
   *
   * Returns the queue it built, or null when the selection held nothing
   * dialable at all - the caller has to say so, because a button that appears
   * to do nothing is worse than one that refuses out loud.
   */
  startQueue: (rows: readonly CallTarget[]) => CallQueue | null;
  /** Dial the next number. Ends the run when there is no next number. */
  nextInQueue: () => void;
  /** End the run without dialling anything else. */
  stopQueue: () => void;
}

export function useCallQueue(startCall: (target: CallTarget) => void): CallQueueApi {
  const [queue, setQueue] = useState<CallQueue | null>(null);
  const suppressed = useSuppressedNumbers();

  /**
   * Read through the hook rather than captured once: an operator who marks a
   * number mid-run changes this set, and the next `advanceQueue` has to see the
   * change. Falls open when the context is absent (a screen mounted outside the
   * shell) - the server refuses a suppressed number anyway, so the cost is one
   * refused dial rather than a call that should not have happened.
   */
  const isSuppressed = useCallback(
    (e164: string) => !!suppressed?.has(e164),
    [suppressed],
  );

  const startQueue = useCallback(
    (rows: readonly CallTarget[]) => {
      const built = buildQueue(rows, isSuppressed);
      if (!built) return null;
      setQueue(built);
      const first = queueAt(built);
      if (first) startCall(first);
      return built;
    },
    [isSuppressed, startCall],
  );

  // Reads `queue` from the closure rather than from a state updater, and the
  // dial happens outside any updater: this only ever runs from a button press,
  // by which point the last render has the current queue, and an updater that
  // places a phone call is one React re-runs in development.
  const nextInQueue = useCallback(() => {
    if (!queue) return;
    const next = advanceQueue(queue, isSuppressed);
    setQueue(next);
    const target = next && queueAt(next);
    if (target) startCall(target);
  }, [queue, isSuppressed, startCall]);

  const stopQueue = useCallback(() => setQueue(null), []);

  return { queue, startQueue, nextInQueue, stopQueue };
}
